import React, { memo, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from '@picsio/ui';

import picsioConfig from '../../../../../../../config';
import localization from '../../../../shared/strings';
import {
  filterItemsByMappedTitles,
  filterItemsByScope,
  generateGroups,
  getMappedTitlesFromDocument,
  GROUP_TITLES_BY_SCOPE,
} from '../../../../store/helpers/productAttributes';
import resolveSalesChannelId from '../../../../store/products/helpers/resolveSalesChannelId';
import { getSavedSalesChannelMapping } from '../../../../store/salesChannels';
import { selectSavedSalesChannelMappingState } from '../../../../store/selectors/salesChannelMappingSelectors';
import DetailsBlock from '../../../DetailsBlock';
import { aggregatedMetaFieldsPropType } from '../helpers/aggregatedFieldPropTypes';
import AggregatedCustomFieldGroup from './AggregatedCustomFieldGroup';
import styles from '../../ProductMetafields/ProductMetaFields.module.scss';

const AggregatedMetaSection = ({
  metaFields,
  attributeScope,
  layer,
  channelId,
  readOnly,
  readOnlyTooltip,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
  blockTitle,
  dataQa,
}) => {
  const dispatch = useDispatch();
  const items = useSelector((state) => state.productAttributes.items || []);
  const teamSalesChannels = useSelector((state) => state.user?.team?.salesChannels || []);

  const salesChannelId = useMemo(
    () => (layer === 'listing' ? resolveSalesChannelId(channelId, teamSalesChannels) : null),
    [channelId, layer, teamSalesChannels],
  );

  const mappingState = useSelector(
    (state) => selectSavedSalesChannelMappingState(state, salesChannelId),
  );

  useEffect(() => {
    if (layer !== 'listing' || !salesChannelId) return;
    if (mappingState.status !== 'idle') return;

    dispatch(getSavedSalesChannelMapping({ salesChannelId }));
  }, [dispatch, layer, mappingState.status, salesChannelId]);

  const mappedTitles = useMemo(() => {
    if (layer !== 'listing') return null;
    return getMappedTitlesFromDocument(mappingState.document, attributeScope);
  }, [attributeScope, layer, mappingState.document]);

  const visibleGroups = useMemo(() => {
    const scopeItems = filterItemsByScope(
      items.filter((item) => !item.isTmp),
      attributeScope,
    );
    if (!scopeItems.length) return [];

    if (layer === 'master') {
      return generateGroups(scopeItems, GROUP_TITLES_BY_SCOPE[attributeScope]);
    }

    if (!mappedTitles?.size) return [];

    const mappedItems = filterItemsByMappedTitles(scopeItems, mappedTitles);
    if (!mappedItems.length) return [];

    return generateGroups(mappedItems, GROUP_TITLES_BY_SCOPE[attributeScope]);
  }, [attributeScope, items, layer, mappedTitles]);

  if (!picsioConfig.isPim || !metaFields) return null;

  if (layer === 'listing') {
    if (!channelId || !salesChannelId) return null;
    if (mappingState.status === 'loading' || mappingState.status === 'idle') {
      return (
        <DetailsBlock
          dataQa={dataQa || 'aggregatedProductCustomFields'}
          detailsPanelVisibility={detailsPanelVisibility}
          toggleVisibility={toggleVisibility}
          blockName={blockName}
          blockTitle={blockTitle || localization.PRODUCT_INFO.customAttributes}
          openByDefault
        >
          <Spinner text="Loading data..." />
        </DetailsBlock>
      );
    }
    if (mappingState.status === 'error') return null;
    if (!mappedTitles?.size) return null;
  }

  if (!visibleGroups.length) return null;

  const resolvedBlockTitle = blockTitle || localization.PRODUCT_INFO.customAttributes;
  const resolvedDataQa = dataQa || 'aggregatedProductCustomFields';

  return (
    <DetailsBlock
      dataQa={resolvedDataQa}
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={resolvedBlockTitle}
    >
      <div className={styles.groups}>
        {visibleGroups.map((group) => (
          <AggregatedCustomFieldGroup
            key={group[0]?.title || group[0]?.id || 'default'}
            group={group}
            metaFields={metaFields}
            readOnly={readOnly}
            readOnlyTooltip={readOnlyTooltip}
            detailsPanelVisibility={detailsPanelVisibility}
            toggleVisibility={toggleVisibility}
          />
        ))}
      </div>
    </DetailsBlock>
  );
};

AggregatedMetaSection.propTypes = {
  metaFields: aggregatedMetaFieldsPropType,
  attributeScope: PropTypes.string.isRequired,
  layer: PropTypes.oneOf(['master', 'listing']).isRequired,
  channelId: PropTypes.string,
  readOnly: PropTypes.bool,
  readOnlyTooltip: PropTypes.string,
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  blockName: PropTypes.string.isRequired,
  blockTitle: PropTypes.string,
  dataQa: PropTypes.string,
};

AggregatedMetaSection.defaultProps = {
  metaFields: null,
  channelId: null,
  readOnly: false,
  readOnlyTooltip: null,
  blockTitle: null,
  dataQa: null,
};

export default memo(AggregatedMetaSection);
